import React, { useState, useCallback, useEffect, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
// import GreatHall from "./GreatHall";
import {
  GitHubIcon, LeetCodeIcon, LinkedInIcon, TwitterIcon, MailIcon, BlogIcon,
} from "../../assets/icons/SocialIcons";


/* The fixed navbar overlays the top of the hero, so every ceiling layer
   (sky, banners, candles, stars) starts below it rather than behind it. */
const NAV_H = "64px";

/* ==================== DATA (from portfolio.md ONLY) ==================== */


const SOCIALS = [
  { label: "GitHub", url: "https://github.com/jugshaurya", Icon: GitHubIcon, color: "#f0f0f0" },
  { label: "LeetCode", url: "https://leetcode.com/u/jugshaurya/", Icon: LeetCodeIcon, color: "#ffa116" },
  { label: "LinkedIn", url: "https://www.linkedin.com/in/jugshaurya/", Icon: LinkedInIcon, color: "#0a66c2" },
  { label: "Twitter", url: "https://twitter.com/jugshaurya", Icon: TwitterIcon, color: "#f0f0f0" },
  { label: "Blogs", url: "https://notes.jugshaurya.vercel.app/", Icon: BlogIcon, color: "#4ecdc4" },
  { label: "Email", url: "mailto:shauryasinghal84@gmail.com", Icon: MailIcon, color: "#ea4335" },
];

/* The eight achievements, sorted into houses. Each hourglass shows its
   headline stat; hovering pours out the rest. */
/* Eight achievements, hung in the ceiling as a constellation.
   x/y are percentages across the sky band; `mag` drives star size. */


const SHATTERED_STATS = [
  // Row 1 — Competitive Programming
  { label: "LeetCode Guardian", value: "2244", sub: "Max Rating · Top 0.8%", highlight: true },
  { label: "Best Contest Rank", value: "264th", sub: "40K+ participants", highlight: false },
  { label: "Problems Solved", value: "1700+", sub: "Multi-platform via Codolio", highlight: false },
  { label: "Google Kick Start", value: "632nd", sub: "Round H · 2022", highlight: false },
  // Row 2 — Academic & Others
  { label: "GATE'21", value: "AIR 2547", sub: "Top 3%", highlight: false },
  { label: "DU Entrance", value: "AIR 13", sub: "20K+ candidates · 2020", highlight: false },
  { label: "BOSS'20", value: "Rank 6", sub: "Bountiful Open Source Summer", highlight: false },
  { label: "Contest Ranks", value: "391st, 394th", sub: "460th, 491st · Weekly Contests", highlight: false },
];

const SHARD_POS = [
  // Row 1 — CP stats: spread wide, upper area
  { x: -130, y: -55, rot: -22 }, { x: 100, y: -70, rot: 15 },
  { x: -70, y: -25, rot: 28 },   { x: 140, y: -15, rot: -18 },
  // Row 2 — Academic stats: spread wide, lower area
  { x: -110, y: 30, rot: 20 },   { x: 120, y: 20, rot: -25 },
  { x: -60, y: 65, rot: -15 },   { x: 80, y: 60, rot: 22 },
];

/* ============ ACROMANTULA WEBBING (Forbidden Forest) ============ */

const AcromantulaWeb: React.FC<{side: "left" | "right"}> = ({ side }) => (
  <WebContainer $side={side}>
    <svg viewBox="0 0 200 200" fill="none" style={{ width: "100%", height: "100%" }}>
      {/* Radial threads from corner */}
      {Array.from({ length: 8 }).map((_, i) => {
        const angle = (i * 12 + (side === "left" ? 0 : 90)) * Math.PI / 180;
        const x2 = (side === "left" ? 0 : 200) + Math.cos(angle) * 250;
        const y2 = Math.sin(angle) * 250;
        return <line key={`r${i}`} x1={side === "left" ? 0 : 200} y1={0} x2={x2} y2={y2} stroke="rgba(255,255,255,0.06)" strokeWidth="0.8" />;
      })}
      {/* Spiral threads */}
      {[40, 80, 120, 160].map((r, i) => (
        <path key={`s${i}`}
          d={side === "left"
            ? `M 0 ${r * 0.3} Q ${r * 0.5} ${r * 0.4} ${r * 0.6} ${r * 0.7}`
            : `M 200 ${r * 0.3} Q ${200 - r * 0.5} ${r * 0.4} ${200 - r * 0.6} ${r * 0.7}`}
          stroke="rgba(255,255,255,0.04)" strokeWidth="0.6" fill="none" />
      ))}
      {/* Web intersections — small dots */}
      {[30, 60, 90, 120].map((r, j) =>
        Array.from({ length: 3 }).map((_, i) => {
          const angle = (i * 15 + 5 + (side === "left" ? 0 : 75)) * Math.PI / 180;
          const cx = (side === "left" ? 0 : 200) + Math.cos(angle) * r;
          const cy = Math.sin(angle) * r;
          return <circle key={`d${j}${i}`} cx={cx} cy={cy} r="1" fill="rgba(255,255,255,0.05)" />;
        })
      )}
    </svg>
  </WebContainer>
);

/* ==================== GOLDEN SNITCH ==================== */

const GoldenSnitch: React.FC<{ onCatch: () => void }> = ({ onCatch }) => {
  const ref = useRef<HTMLDivElement>(null);
  const t = useRef(0);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (reduced) {
      // Park it somewhere sensible instead of flying
      if (ref.current) ref.current.style.transform = "translate(85vw, 22vh)";
      return;
    }
    let raf: number;
    const animate = () => {
      t.current += 0.006;
      const cx = window.innerWidth / 2, cy = window.innerHeight / 2;
      const rx = Math.min(window.innerWidth * 0.4, 400);
      const ry = Math.min(window.innerHeight * 0.32, 220);
      const x = cx + Math.sin(t.current * 1.1) * rx + Math.sin(t.current * 3.3) * 40;
      const y = cy + Math.sin(t.current * 0.7) * ry + Math.cos(t.current * 2.5) * 30;
      if (ref.current) ref.current.style.transform = `translate(${x}px, ${y}px) rotate(${Math.sin(t.current * 3) * 20}deg)`;
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [reduced]);

  return (
    <SnitchWrap ref={ref} onClick={onCatch} title="Catch the Snitch">
      <SnitchBall /><SnitchWingL /><SnitchWingR /><SnitchGlow />
    </SnitchWrap>
  );
};

/* ==================== THE GREAT HALL ==================== */

const EnchantedCeiling: React.FC = () => (
  <Ceiling aria-hidden="true">
    <MoonGlow />
    <CloudDrift $i={0} />
    <CloudDrift $i={1} />
    <CloudDrift $i={2} />
  </Ceiling>
);

/* Cinzel turns lowercase into small capitals, so "264th" reads as "264TH".
   Split the suffix out and set it in Raleway, superscripted. */
function withOrdinals(text: string): React.ReactNode {
  const parts = text.split(/(\d+(?:st|nd|rd|th))/gi);
  return parts.map((part, i) => {
    const m = /^(\d+)(st|nd|rd|th)$/i.exec(part);
    if (!m) return <React.Fragment key={i}>{part}</React.Fragment>;
    return (
      <React.Fragment key={i}>
        {m[1]}<Ord>{m[2].toLowerCase()}</Ord>
      </React.Fragment>
    );
  });
}

const ShatteredStats: React.FC = () => {
  const [assembled, setAssembled] = useState(false);
  return (
    <ShatterSection onMouseEnter={() => setAssembled(true)} onMouseLeave={() => setAssembled(false)}>
      <ShatterHint $visible={!assembled}>&#9670; Hover to restore order from chaos &#9670;</ShatterHint>
      <ShardsContainer $assembled={assembled}>
        {SHATTERED_STATS.map((stat, i) => (
          <Shard key={stat.label} $i={i} $assembled={assembled} $x={SHARD_POS[i].x} $y={SHARD_POS[i].y} $rot={SHARD_POS[i].rot}>
            <ShardGlass $assembled={assembled} $highlight={stat.highlight} />
            <ShardContent $assembled={assembled}>
              <ShardValue $highlight={stat.highlight}>{withOrdinals(stat.value)}</ShardValue>
              <ShardLabel>{stat.label}</ShardLabel>
              <ShardSub>{withOrdinals(stat.sub)}</ShardSub>
            </ShardContent>
          </Shard>
        ))}
      </ShardsContainer>
      {!assembled && <><ChaosP $x={15} $y={20} $s={3} $d={4} /><ChaosP $x={80} $y={35} $s={2} $d={5} /><ChaosP $x={45} $y={70} $s={4} $d={3.5} /></>}
    </ShatterSection>
  );
};

const Hero: React.FC = () => {
  const [spotX, setSpotX] = useState(50);
  const [spotY, setSpotY] = useState(50);
  const [snitchCaught, setSnitchCaught] = useState(false);
  const [showCaught, setShowCaught] = useState(false);
  const reduced = usePrefersReducedMotion();

  const handleMouse = useCallback((e: React.MouseEvent) => {
    if (reduced) return;
    setSpotX((e.clientX / window.innerWidth) * 100);
    setSpotY((e.clientY / window.innerHeight) * 100);
  }, [reduced]);

  const catchSnitch = useCallback(() => {
    setSnitchCaught(true);
    setShowCaught(true);
    window.setTimeout(() => setShowCaught(false), 3200);
  }, []);

  // The Snitch is released again after a short while
  useEffect(() => {
    if (!snitchCaught) return;
    // released again shortly after, so it's always catchable
    const id = window.setTimeout(() => setSnitchCaught(false), 3000);
    return () => window.clearTimeout(id);
  }, [snitchCaught]);

  return (
    <HeroSection id="hero" onMouseMove={handleMouse}>
      <SpaceBg />
      <Nebula $x={20} $y={30} $color="rgba(180,50,50,0.04)" $size={500} />
      <Nebula $x={75} $y={60} $color="rgba(50,80,180,0.03)" $size={400} />
      <Nebula $x={50} $y={15} $color="rgba(130,50,160,0.03)" $size={350} />
      <Spotlight $x={spotX} $y={spotY} />
      <CrimsonCore />

      {/* Enchanted ceiling */}
      <EnchantedCeiling />

      {/* Acromantula webbing in the corners */}
      <AcromantulaWeb side="left" />
      <AcromantulaWeb side="right" />

      {/* Flying creatures */}
      {!snitchCaught && <GoldenSnitch onCatch={catchSnitch} />}
      {showCaught && (
        <CaughtToast>
          You caught the Snitch &mdash; 150 points to Gryffindor!
        </CaughtToast>
      )}

      {/* Social rail */}
      <SocialRail>
        {SOCIALS.map((s) => (
          <SocialRailLink key={s.label} href={s.url} $color={s.color}
            target={s.url.startsWith("mailto") ? undefined : "_blank"}
            rel={s.url.startsWith("mailto") ? undefined : "noopener noreferrer"}
            aria-label={s.label}><s.Icon size={22} /></SocialRailLink>
        ))}
        <SocialRailLine />
      </SocialRail>

      <HeroContent>
        <Greeting>Hi, I'm</Greeting>
        <LiquidName>Shaurya Singhal</LiquidName>

        {/* An Elder-Wand underline, lit at the tip */}
        <WandRule>
          <svg viewBox="0 0 420 44" aria-hidden="true">
            <defs>
              <linearGradient id="wd_wood" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#2a1a0d" />
                <stop offset="20%" stopColor="#5a3d1f" />
                <stop offset="55%" stopColor="#7a5528" />
                <stop offset="82%" stopColor="#9c6f34" />
                <stop offset="100%" stopColor="#c99a4e" />
              </linearGradient>
              <linearGradient id="wd_sheen" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(255,235,190,0.5)" />
                <stop offset="45%" stopColor="rgba(255,225,170,0.06)" />
                <stop offset="100%" stopColor="rgba(0,0,0,0.35)" />
              </linearGradient>
              <radialGradient id="wd_lumos">
                <stop offset="0%" stopColor="rgba(255,252,235,0.95)" />
                <stop offset="28%" stopColor="rgba(255,232,160,0.55)" />
                <stop offset="65%" stopColor="rgba(255,196,90,0.18)" />
                <stop offset="100%" stopColor="rgba(255,180,70,0)" />
              </radialGradient>
            </defs>

            {/* handle — carved grip at the left */}
            <path d="M6 18 Q2 22 6 26 L34 25.4 L34 18.6 Z" fill="#241408" />
            <rect x="30" y="18.2" width="46" height="7.6" rx="3.4" fill="#301b0c" />
            {[38, 47, 56, 65].map((x) => (
              <rect key={x} x={x} y="17.6" width="2.4" height="8.8" rx="1.2" fill="#170c05" opacity="0.85" />
            ))}
            <rect x="74" y="17.4" width="7" height="9.2" rx="3" fill="#8a6a2a" />

            {/* shaft, tapering to the tip */}
            <path d="M80 17.6 L372 20.6 L372 23.4 L80 26.4 Z" fill="url(#wd_wood)" />
            <path d="M80 17.6 L372 20.6 L372 22 L80 21.4 Z" fill="url(#wd_sheen)" />

            {/* the Elder Wand's nodules */}
            {[118, 168, 218, 268, 316].map((x, i) => (
              <g key={x}>
                <ellipse cx={x} cy={22 + i * 0.12} rx={7.5 - i * 0.75} ry={6.4 - i * 0.62}
                  fill="url(#wd_wood)" />
                <ellipse cx={x} cy={20.4 + i * 0.12} rx={5.4 - i * 0.55} ry={2 - i * 0.16}
                  fill="rgba(255,228,175,0.22)" />
              </g>
            ))}

            {/* tip */}
            <path d="M372 20.6 L392 21.9 L372 23.2 Z" fill="#d8ac5e" />
          </svg>

          <Lumos />
          {[0, 1, 2, 3, 4].map((i) => <Spark key={i} $i={i} />)}
        </WandRule>

        <WandCaption>
          MTS-2 <CapAt>@</CapAt> <CapAdobe>Adobe</CapAdobe>
        </WandCaption>

        <ShatteredStats />





      </HeroContent>

      <ScrollIndicator href="#about" onClick={(e) => {
        e.preventDefault();
        document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
      }}>
        <ScrollRune>&#10023;</ScrollRune>
        <ScrollText>explore</ScrollText>
      </ScrollIndicator>
    </HeroSection>
  );
};

export default Hero;

/* ===================== ANIMATIONS ===================== */
const fadeUp = keyframes`from{opacity:0;transform:translateY(25px)}to{opacity:1;transform:translateY(0)}`;
const floatBounce = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(8px)}`;
const glowPulse = keyframes`0%,100%{text-shadow:0 0 8px rgba(200,100,100,0.3)}50%{text-shadow:0 0 20px rgba(200,100,100,0.6)}`;
const wingFlap = keyframes`0%,100%{transform:scaleX(1) rotateY(0)}20%{transform:scaleX(0.2) rotateY(70deg)}40%{transform:scaleX(1) rotateY(0)}60%{transform:scaleX(0.2) rotateY(-70deg)}80%{transform:scaleX(1) rotateY(0)}`;
const snitchGlow = keyframes`0%,100%{box-shadow:0 0 10px rgba(205,155,50,0.6),0 0 25px rgba(205,155,50,0.3),0 0 50px rgba(205,155,50,0.1)}50%{box-shadow:0 0 18px rgba(205,155,50,0.9),0 0 40px rgba(205,155,50,0.4),0 0 70px rgba(205,155,50,0.15)}`;

/* ===================== BACKGROUND ===================== */
const HeroSection = styled.section`position:relative;width:100%;min-height:100vh;display:flex;align-items:center;justify-content:center;overflow:hidden;background:#030108;`;
const SpaceBg = styled.div`position:absolute;inset:0;background:radial-gradient(ellipse at 50% 50%,rgba(10,5,20,1) 0%,#030108 100%);`;
const Nebula = styled.div<{$x:number;$y:number;$color:string;$size:number}>`position:absolute;left:${({$x})=>$x}%;top:${({$y})=>$y}%;width:${({$size})=>$size}px;height:${({$size})=>$size}px;border-radius:50%;background:radial-gradient(circle,${({$color})=>$color},transparent 70%);filter:blur(60px);pointer-events:none;`;
const CrimsonCore = styled.div`position:absolute;top:50%;left:50%;width:300px;height:300px;border-radius:50%;background:radial-gradient(circle,rgba(180,40,40,0.08),transparent 70%);pointer-events:none;z-index:1;`;
const Spotlight = styled.div<{$x:number;$y:number}>`position:absolute;inset:0;z-index:2;background:radial-gradient(400px circle at ${({$x})=>$x}% ${({$y})=>$y}%,rgba(180,50,50,0.02),transparent 60%);pointer-events:none;`;










/* maker's stamp, pressed faintly into the corner */

/* the wand itself, laid along the bottom of the label */










/* a blot where the quill sat too long */



/* ============ THE WAND (underline beneath the name) ============ */

const lumosPulse = keyframes`
  0%, 100% { opacity: 0.55; transform: translate(-50%, -50%) scale(1); }
  50%      { opacity: 0.8;  transform: translate(-50%, -50%) scale(1.1); }
`;

const wandBreathe = keyframes`
  0%, 100% { transform: rotate(-0.35deg) translateY(0); }
  50%      { transform: rotate(0.35deg) translateY(-1.5px); }
`;

const sparkRise = keyframes`
  0%   { opacity: 0; transform: translate(0, 0) scale(0.4); }
  20%  { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--sx), var(--sy)) scale(0.15); }
`;

const WandRule = styled.div`
  position: relative;
  width: min(430px, 82vw);
  margin: 0.5rem 0 0.15rem;
  transform-origin: 20% 50%;
  animation: ${wandBreathe} 6s ease-in-out infinite;

  svg {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
    filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.6));
  }
`;

/* Lumos — the lit tip */
const Lumos = styled.span`
  position: absolute;
  top: 50%;
  left: 93.5%;
  width: 78px;
  height: 78px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle,
    rgba(255,250,230,0.55) 0%,
    rgba(255,232,160,0.20) 24%,
    rgba(255,196,90,0.06) 55%,
    rgba(255,180,70,0) 74%);
  animation: ${lumosPulse} 3.2s ease-in-out infinite;
  pointer-events: none;
  mix-blend-mode: screen;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 4.5px;
    height: 4.5px;
    margin: -2.25px 0 0 -2.25px;
    border-radius: 50%;
    background: #fff8e4;
    box-shadow: 0 0 7px 2px rgba(255, 244, 200, 0.55);
  }
`;

const Spark = styled.span<{$i:number}>`
  position: absolute;
  top: 50%;
  left: 93.5%;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #ffe9a8;
  box-shadow: 0 0 5px rgba(255, 220, 140, 0.6);
  pointer-events: none;
  --sx: ${({$i}) => [16, 26, 8, 30, 20][$i]}px;
  --sy: ${({$i}) => [-26, -14, -34, -22, -40][$i]}px;
  animation: ${sparkRise} ${({$i}) => 2.2 + $i * 0.45}s ease-out infinite;
  animation-delay: ${({$i}) => $i * 0.55}s;
`;

const WandCaption = styled.p`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.34rem;
  margin-top: 0.15rem;
  font-family: "JetBrains Mono", monospace;
  font-size: clamp(0.6rem, 1.3vw, 0.75rem);
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: #9c8d84;
  animation: ${fadeUp} 0.5s ease 0.25s both;
`;

const CapAt = styled.span`
  font-family: "JetBrains Mono", monospace;
  text-transform: none;
  color: #6f6058;
`;

const CapAdobe = styled.span`
  font-family: "Cinzel", serif;
  font-size: 1.55em;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: none;
  color: #e0605c;
  text-shadow: 0 0 16px rgba(224, 96, 92, 0.25);
`;

/* ===================== SHATTERED STATS ===================== */
const chaosSpark = keyframes`0%,100%{opacity:0.3;transform:scale(1)}50%{opacity:0.8;transform:scale(1.5)}`;

const ShatterSection = styled.div`position:relative;width:100%;max-width:700px;min-height:200px;margin-top:3.2rem;margin-bottom:1rem;animation:${fadeUp} 0.6s ease 0.25s both;cursor:pointer;`;

const ShatterHint = styled.p<{$visible:boolean}>`text-align:center;font-family:"Cinzel",serif;font-size:0.6rem;color:rgba(180,100,100,0.35);letter-spacing:0.2em;margin-bottom:0.5rem;transition:opacity 0.3s;opacity:${({$visible})=>$visible?1:0};`;

const ShardsContainer = styled.div<{$assembled:boolean}>`display:flex;flex-wrap:wrap;justify-content:center;gap:${({$assembled})=>$assembled?"0.6rem":"0"};position:relative;min-height:180px;align-items:center;transition:gap 0.6s ease;`;

const Shard = styled.div<{$i:number;$assembled:boolean;$x:number;$y:number;$rot:number}>`position:relative;width:140px;padding:0.8rem;transition:all 0.7s cubic-bezier(0.34,1.56,0.64,1);${({$assembled,$x,$y,$rot})=>!$assembled&&css`transform:translate(${$x}px,${$y}px) rotate(${$rot}deg) scale(0.85);opacity:0.5;`}${({$assembled})=>$assembled&&css`transform:translate(0,0) rotate(0deg) scale(1);opacity:1;`}`;

const ShardGlass = styled.div<{$assembled:boolean;$highlight?:boolean}>`
  position: absolute; inset: 0;
  background: ${({$assembled,$highlight}) => $assembled ? ($highlight ? "rgba(255,161,22,0.08)" : "rgba(15,8,25,0.7)") : "linear-gradient(135deg,rgba(180,50,50,0.08),rgba(60,30,80,0.06))"};
  border: 1px solid ${({$assembled,$highlight}) => $assembled ? ($highlight ? "rgba(255,161,22,0.3)" : "rgba(180,100,100,0.15)") : "rgba(180,50,50,0.1)"};
  border-radius: ${({$assembled}) => $assembled ? "8px" : "2px"};
  clip-path: ${({$assembled}) => $assembled ? "none" : "polygon(8% 0%,100% 4%,92% 100%,0% 96%)"};
  transition: all 0.7s cubic-bezier(0.34,1.56,0.64,1);
  backdrop-filter: ${({$assembled}) => $assembled ? "blur(8px)" : "none"};
  ${({$assembled,$highlight}) => $assembled && $highlight && `box-shadow: 0 0 20px rgba(255,161,22,0.08);`}
`;

const ShardContent = styled.div<{$assembled:boolean}>`position:relative;z-index:1;text-align:center;transition:all 0.5s ease;opacity:${({$assembled})=>$assembled?1:0.4};`;

const ShardValue = styled.div<{$highlight?:boolean}>`
  font-family: "Cinzel", serif;
  font-size: ${({$highlight}) => $highlight ? "1.4rem" : "1.1rem"};
  font-weight: 700;
  color: ${({$highlight}) => $highlight ? "#ffa116" : "#e8c8c8"};
  text-shadow: ${({$highlight}) => $highlight ? "0 0 15px rgba(255,161,22,0.4)" : "0 0 10px rgba(180,80,80,0.2)"};
`;

const Ord = styled.sup`
  font-family: "Raleway", sans-serif;
  font-size: 0.5em;
  font-weight: 600;
  text-transform: lowercase;
  letter-spacing: 0;
  vertical-align: super;
  margin-left: 0.06em;
`;

const ShardLabel = styled.div`font-family:"Raleway",sans-serif;font-size:0.65rem;color:#b8a0a0;letter-spacing:0.08em;margin-top:0.15rem;`;

const ShardSub = styled.div`font-family:"JetBrains Mono",monospace;font-size:0.55rem;color:#8a7070;margin-top:0.1rem;`;

const ChaosP = styled.div<{$x:number;$y:number;$s:number;$d:number}>`position:absolute;left:${({$x})=>$x}%;top:${({$y})=>$y}%;width:${({$s})=>$s}px;height:${({$s})=>$s}px;border-radius:50%;background:rgba(200,80,80,0.4);animation:${chaosSpark} ${({$d})=>$d}s ease-in-out infinite;pointer-events:none;filter:blur(1px);`;

























/* ============ HOUSE POINTS ============ */












const caughtIn = keyframes`
  0%   { opacity: 0; transform: translate(-50%, 10px) scale(0.94); }
  12%  { opacity: 1; transform: translate(-50%, 0) scale(1); }
  82%  { opacity: 1; }
  100% { opacity: 0; transform: translate(-50%, -8px); }
`;

const CaughtToast = styled.div`
  position: fixed;
  left: 50%;
  bottom: 12vh;
  z-index: 300;
  transform: translateX(-50%);
  padding: 0.65rem 1.3rem;
  border-radius: 100px;
  font-family: "Cinzel", serif;
  font-size: 0.78rem;
  color: #f4e3bd;
  white-space: nowrap;
  pointer-events: none;
  border: 1px solid rgba(211, 166, 37, 0.5);
  background: rgba(24, 10, 12, 0.92);
  box-shadow: 0 0 30px rgba(211, 166, 37, 0.28);
  backdrop-filter: blur(8px);
  animation: ${caughtIn} 3.2s ease both;

  @media (max-width: 600px) { font-size: 0.62rem; white-space: normal; text-align: center; }
`;












/* the rod it hangs from, reaching up toward the candles */








/* ===================== THE GREAT HALL ===================== */

const cloudDrift = keyframes`
  from { transform: translateX(-12%); }
  to   { transform: translateX(112%); }
`;

const moonBreathe = keyframes`
  0%, 100% { opacity: 0.45; transform: scale(1); }
  50%      { opacity: 0.75; transform: scale(1.06); }
`;






/* --- enchanted ceiling --- */
const Ceiling = styled.div`
  position: absolute;
  top: ${NAV_H};
  left: 0;
  right: 0;
  height: 62%;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;
  mask-image: linear-gradient(to bottom, #000 0%, #000 55%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, #000 0%, #000 55%, transparent 100%);
`;

const MoonGlow = styled.div`
  position: absolute;
  top: 6%;
  left: 68%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(200, 215, 255, 0.16), rgba(160, 180, 235, 0.05) 45%, transparent 72%);
  filter: blur(6px);
  animation: ${moonBreathe} 9s ease-in-out infinite;
`;

const CloudDrift = styled.div<{$i:number}>`
  position: absolute;
  top: ${({$i}) => 8 + $i * 15}%;
  left: 0;
  width: ${({$i}) => 260 + $i * 90}px;
  height: ${({$i}) => 60 + $i * 18}px;
  border-radius: 50%;
  background: radial-gradient(ellipse, rgba(120, 130, 175, 0.07), transparent 70%);
  filter: blur(14px);
  animation: ${cloudDrift} ${({$i}) => 90 + $i * 45}s linear infinite;
  animation-delay: ${({$i}) => $i * -30}s;
`;




























/* ===================== ACROMANTULA WEBBING ===================== */
const WebContainer = styled.div<{$side:string}>`
  position: absolute;
  top: 0;
  ${({$side}) => $side === "left" ? "left: 0;" : "right: 0;"}
  width: 250px;
  height: 250px;
  z-index: 2;
  pointer-events: none;
  opacity: 0.8;

  @media (max-width: 768px) {
    width: 175px;
    height: 175px;
  }
`;

/* ===================== QUIDDITCH GOAL POSTS ===================== */




/* ===================== GOLDEN SNITCH ===================== */
const SnitchWrap = styled.div`position:absolute;z-index:60;pointer-events:auto;cursor:none;will-change:transform;top:0;left:0;margin-left:-20px;margin-top:-15px;`;
const SnitchBall = styled.div`width:18px;height:18px;border-radius:50%;background:radial-gradient(circle at 30% 30%,#e8c860,#cd9b32,#a07828);animation:${snitchGlow} 1.2s ease-in-out infinite;position:relative;z-index:2;border:1px solid rgba(205,155,50,0.4);&::after{content:"";position:absolute;top:50%;left:2px;right:2px;height:1px;background:rgba(160,120,40,0.5);border-radius:1px;}`;
const SnitchWingL = styled.div`position:absolute;top:-10px;left:-22px;width:28px;height:20px;z-index:1;animation:${wingFlap} 0.25s ease-in-out infinite;transform-origin:right center;background:linear-gradient(135deg,rgba(255,255,255,0.05),rgba(255,255,255,0.5) 30%,rgba(220,210,190,0.7) 60%,rgba(255,255,255,0.3));clip-path:polygon(100% 50%,70% 10%,30% 0%,0% 15%,0% 45%,10% 70%,40% 100%,70% 90%);&::before{content:"";position:absolute;inset:0;background:repeating-linear-gradient(60deg,transparent,transparent 3px,rgba(200,180,140,0.15) 3px,rgba(200,180,140,0.15) 4px);clip-path:inherit;}`;
const SnitchWingR = styled.div`position:absolute;top:-10px;right:-22px;width:28px;height:20px;z-index:1;animation:${wingFlap} 0.25s ease-in-out infinite;animation-delay:0.05s;transform-origin:left center;background:linear-gradient(225deg,rgba(255,255,255,0.05),rgba(255,255,255,0.5) 30%,rgba(220,210,190,0.7) 60%,rgba(255,255,255,0.3));clip-path:polygon(0% 50%,30% 10%,70% 0%,100% 15%,100% 45%,90% 70%,60% 100%,30% 90%);&::before{content:"";position:absolute;inset:0;background:repeating-linear-gradient(120deg,transparent,transparent 3px,rgba(200,180,140,0.15) 3px,rgba(200,180,140,0.15) 4px);clip-path:inherit;}`;
const SnitchGlow = styled.div`position:absolute;top:-8px;left:-8px;width:34px;height:34px;border-radius:50%;background:radial-gradient(circle,rgba(205,155,50,0.15),transparent 70%);filter:blur(5px);`;

/* ===================== PATRONUS ===================== */






/* ===================== SOCIAL RAIL ===================== */
const SocialRail = styled.div`position:fixed;left:1.5rem;bottom:0;z-index:10;display:flex;flex-direction:column;align-items:center;gap:1rem;@media(max-width:768px){display:none;}`;
const SocialRailLink = styled.a<{$color:string}>`color:${({$color})=>$color};opacity:1;transition:all 0.3s ease;display:flex;filter:drop-shadow(0 0 5px ${({$color})=>$color}45);&:hover{opacity:0.45;filter:grayscale(1) brightness(0.7);transform:translateY(-3px);}`;
const SocialRailLine = styled.div`width:1px;height:80px;background:linear-gradient(180deg,rgba(180,100,100,0.2),transparent);margin-top:0.5rem;`;



















/* ===================== LAYOUT ===================== */
const HeroContent = styled.div`position:relative;z-index:7;display:flex;flex-direction:column;align-items:center;text-align:center;padding:5rem 2rem 4rem;max-width:900px;width:100%;`;
const Greeting = styled.p`font-family:"Raleway",sans-serif;font-size:0.9rem;color:#6a5050;letter-spacing:0.2em;margin-bottom:0.2rem;animation:${fadeUp} 0.5s ease both;`;
const liquidFlow = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const LiquidName = styled.h1`
  font-family: "Cinzel", serif;
  font-size: clamp(2rem, 5.5vw, 3.8rem);
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.1;
  margin-bottom: 0.4rem;
  animation: ${fadeUp} 0.5s ease 0.05s both;

  /* Liquid flowing gradient inside text */
  background: linear-gradient(
    90deg,
    #e8d8d8 0%,
    #ff6b6b 15%,
    #ffd700 30%,
    #4ecdc4 45%,
    #a18cd1 60%,
    #ff6b6b 75%,
    #e8d8d8 100%
  );
  background-size: 300% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${fadeUp} 0.5s ease 0.05s both, ${liquidFlow} 6s ease-in-out infinite;

  /* Fallback glow */
  filter: drop-shadow(0 0 20px rgba(180,80,80,0.1));

  &:hover {
    animation: ${fadeUp} 0.5s ease 0.05s both, ${liquidFlow} 2s ease-in-out infinite;
  }
`;

const ScrollIndicator = styled.a`position:absolute;bottom:1.5rem;left:50%;transform:translateX(-50%);z-index:7;display:flex;flex-direction:column;align-items:center;gap:0.3rem;animation:${floatBounce} 2.5s ease-in-out infinite;cursor:pointer;text-decoration:none;`;
const ScrollRune = styled.span`font-size:1.2rem;color:rgba(180,100,100,0.4);animation:${glowPulse} 3s ease-in-out infinite;`;
const ScrollText = styled.span`font-family:"Cinzel",serif;font-size:0.5rem;color:#3a2525;letter-spacing:0.3em;`;
